/* eslint-disable max-lines-per-function */
/* eslint-disable max-params */
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { CommandContribution, CommandRegistry } from '@patsnap/tahiti-core';
import { createDialog } from '@patsnap/tahiti-core';
import type { ClassificationType } from '@/types/classification';

const compareTableEditDialogService = createDialog(() => import('@/presentation/pages/novelty-check-report/components/technical-feature-compare-table/TechnicalFeatureCompareTableEditDialog.vue'));
const patentTechnicalFeatureDetailDialogService = createDialog(() => import('@/presentation/pages/novelty-check-report/components/patent-technical-feature-detail/PatentTechnicalFeatureDetailDialog.vue'));
const keywordHelperDialogService = createDialog(() => import('@/presentation/pages/novelty-check-report/components/keyword-helper/KeywordHelperDialog.vue'));
const classificationHelperDialogService = createDialog(() => import('@/presentation/components/serach-helper/classification-helper/ClassificationHelper.vue'));
const technicalFeatureDialogService = createDialog(() => import('@/presentation/pages/novelty-check-report/components/search-element/components/TechnicalFeatureDialog.vue'));
const searchElementTableGroupRenderDialogService = createDialog(() => import('@/presentation/pages/novelty-check-report/components/search-element/SearchElementTableGroupRenderDialog.vue'));
const queryEditorDialogService = createDialog(() => import('@/presentation/pages/novelty-check-report/components/QueryEditor/QueryEditorDialog.vue'));
const minorLanguageTranslationNoticeDialogService = createDialog(() => import('@/presentation/pages/home/components/MinorLanguageTranslationNoticeDialog.vue'));

@injectable()
export class NoveltyCommandContribution implements CommandContribution {
    private _handleOpenEditCompareListDialog(payload: { defaultAddPatent?: boolean }): void {
        const instance = compareTableEditDialogService();
        instance.open(payload);
    }

    private _handleOpenPatentTechnicalFeatureDetailDialog(payload: { patentId: string, lang: string, defaultSelectedTechFeature?: string }): void {
        const instance = patentTechnicalFeatureDetailDialogService();
        instance.open(payload);
    }

    private async _handleOpenKeywordHelperDialog(payload: { keyword: string, expansionWords: string[] }): Promise<void> {
        return new Promise((resolve, reject) => {
            const instance = keywordHelperDialogService();
            instance.onSubmit(async (data, next) => {
                try {
                    resolve(data);
                    await next();
                } catch (error) {
                    reject({ type: 'error', error });
                    next(false);
                }
            });
            instance.onCancel(() => {
                reject({ type: 'close' });
            });
            instance.open(payload);
        });
    }

    private async _handleOpenClassificationHelperDialog(payload: { subType: ClassificationType, selectedItems: string[] }): Promise<string[]> {
        return new Promise((resolve, reject) => {
            const instance = classificationHelperDialogService();
            instance.onSubmit(async (data, next) => {
                resolve(data);
                await next();
            });
            instance.onCancel(() => {
                reject({ type: 'close' });
            });
            instance.open(payload);
        });
    }

    private async _handleOpenTechnicalFeatureDialog(payload: { techFeatures: string[] }): Promise<string[]> {
        return new Promise((resolve, reject) => {
            const instance = technicalFeatureDialogService();
            instance.onSubmit(async (data, next) => {
                resolve(data);
                await next();
            });
            instance.onCancel(() => {
                reject({ type: 'close' });
            });
            instance.open(payload);
        });
    }

    private async _handleOpenSearchElementTableGroupRenderDialog(): Promise<void> {
        const instance = searchElementTableGroupRenderDialogService();
        instance.open();
    }

    private async _handleOpenQueryEditorDialog(): Promise<string | void> {
        return new Promise((resolve, reject) => {
            const instance = queryEditorDialogService();
            instance.onSubmit(async (data, next) => {
                try {
                    resolve(data);
                    await next();
                } catch (error) {
                    reject({ type: 'error', error });
                    next(false);
                }
            });
            instance.onCancel(() => {
                reject({ type: 'close' });
            });
            instance.open();
        });
    }

    private _handleOpenMinorLanguageTranslationNoticeDialog(payload: { translatedText: string }): Promise<{ translatedInput: string }> {
        return new Promise((resolve, reject) => {
            const instance = minorLanguageTranslationNoticeDialogService();
            instance.onSubmit(async (data, next) => {
                try {
                    resolve(data);
                    await next();
                } catch (error) {
                    reject({ type: 'error', error });
                    next(false);
                }
            });
            instance.onCancel(() => {
                reject({ type: 'close' });
            });
            instance.open(payload);
        });
    }

    registerCommands(commands: CommandRegistry): void {
        commands.registerCommand({
            id: 'novelty:open-edit-compare-list-dialog',
        }, {
            execute: async (payload: { defaultAddPatent?: boolean }) => {
                await this._handleOpenEditCompareListDialog(payload);
            },
        });
        commands.registerCommand({
            id: 'novelty:open-patent-technical-feature-detail-dialog',
        }, {
            execute: async (payload: { patentId: string, lang: string, defaultSelectedTechFeature?: string }) => {
                await this._handleOpenPatentTechnicalFeatureDetailDialog(payload);
            },
        });
        commands.registerCommand({
            id: 'novelty:open-keyword-helper-dialog',
        }, {
            execute: (payload: { keyword: string, expansionWords: string[] }) => {
                return this._handleOpenKeywordHelperDialog(payload);
            },
        });
        commands.registerCommand({
            id: 'novelty:open-classification-helper-dialog',
        }, {
            execute: (payload: { subType: ClassificationType, selectedItems: string[] }) => {
                return this._handleOpenClassificationHelperDialog(payload);
            },
        });
        commands.registerCommand({
            id: 'novelty:open-technical-feature-dialog',
        }, {
            execute: (payload: { techFeatures: string[] }) => {
                return this._handleOpenTechnicalFeatureDialog(payload);
            },
        });
        commands.registerCommand({
            id: 'novelty:open-search-element-table-group-render-dialog',
        }, {
            execute: () => {
                return this._handleOpenSearchElementTableGroupRenderDialog();
            },
        });
        commands.registerCommand({
            id: 'novelty:open-query-editor-dialog',
        }, {
            execute: () => {
                return this._handleOpenQueryEditorDialog();
            },
        });
        commands.registerCommand({
            id: 'novelty:open-minor-language-translation-notice-dialog',
        }, {
            execute: (payload: { translatedText: string }) => {
                return this._handleOpenMinorLanguageTranslationNoticeDialog(payload);
            },
        });
    }
}

declare module '@patsnap/tahiti-core' {
    export interface CommandService {
        /**
         * 打开编辑对比列表对话框
         * @param commandId - 命令ID
         * @param payload - 对话框参数
         */
        executeCommand(commandId: 'novelty:open-edit-compare-list-dialog', payload: { defaultAddPatent?: boolean }): void;
        /**
         * 打开专利技术特征详情对话框
         * @param commandId - 命令ID
         */
        executeCommand(commandId: 'novelty:open-patent-technical-feature-detail-dialog',
            payload: {
                patentId: string,
                lang: string,
                defaultSelectedTechFeature?: string,
            }
        ): void;
        /**
         * 打开关键词助手对话框
         * @param commandId - 命令ID
         */
        executeCommand(commandId: 'novelty:open-keyword-helper-dialog', payload: {keyword: string, expansionWords: string[]}): void;
        /**
         * 打开分类助手对话框
         * @param commandId - 命令ID
         */
        executeCommand(commandId: 'novelty:open-classification-helper-dialog', payload: { subType: ClassificationType, selectedItems: string[] }): void;
        /**
         * 打开技术特征对话框
         * @param commandId - 命令ID
         */
        executeCommand(commandId: 'novelty:open-technical-feature-dialog', payload: { techFeatures: string[] }): Promise<string[]>;
        /**
         * 打开搜索元素表组渲染对话框
         * @param commandId - 命令ID
         */
        executeCommand(commandId: 'novelty:open-search-element-table-group-render-dialog'): Promise<void>;
        /**
         * 打开检索式编辑器对话框
         * @param commandId - 命令ID
         */
        executeCommand(commandId: 'novelty:open-query-editor-dialog'): Promise<string | void>;
        /**
         * 打开小语种翻译提示对话框
         * @param commandId - 命令ID
         * @param payload - 对话框参数
         * @returns Promise，resolve时返回用户接受的翻译后文本
         */
        executeCommand(commandId: 'novelty:open-minor-language-translation-notice-dialog', payload: { translatedText: string }): Promise<{ translatedInput: string }>;
    }
}
